import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CarList = ({
  collapsed,
  toggleCollapse,
  projectCars,
  selectedProject,
  getCarUtilization,
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-steel-200 ${collapsed ? 'cursor-pointer hover:bg-steel-50' : ''}`}
      onClick={collapsed ? toggleCollapse : undefined}
    >
      <div
        className={`p-3 border-b border-steel-200 cursor-pointer hover:bg-steel-50 transition-colors ${collapsed ? 'border-b-0' : ''}`}
        onClick={(e) => { e.stopPropagation(); toggleCollapse(); }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center">
              {collapsed ? (
                <div className="w-5 h-5 text-slate-600">
                  <svg className="w-full h-full" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                  </svg>
                </div>
              ) : (
                `Cars (${projectCars.length})`
              )}
            </h2>
            {!collapsed && (
              <p className="text-sm text-slate-600 mt-1">
                Available vehicles for the project
              </p>
            )}
          </div>
          <div className="p-1 text-slate-500">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3">
          {projectCars.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <div className="w-12 h-12 mx-auto mb-4 text-slate-300">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                </svg>
              </div>
              <p>No cars available</p>
              <p className="text-xs text-slate-400 mt-1">
                {selectedProject ? 'No cars assigned for this date' : 'Select a project first'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {projectCars.map(car => {
                const utilization = getCarUtilization ? getCarUtilization(car.id) : 0;

                let bgColorClass = 'bg-white';
                let borderColorClass = 'border-steel-200';

                if (utilization === 100) {
                  bgColorClass = 'bg-green-100';
                  borderColorClass = 'border-green-300';
                } else if (utilization >= 76) {
                  bgColorClass = 'bg-red-50';
                  borderColorClass = 'border-red-200';
                } else if (utilization >= 51) {
                  bgColorClass = 'bg-orange-50';
                  borderColorClass = 'border-orange-200';
                } else if (utilization >= 26) {
                  bgColorClass = 'bg-yellow-50';
                  borderColorClass = 'border-yellow-200';
                }

                return (
                  <div
                    key={car.id}
                    className={`border rounded-xl p-2 hover:shadow-md transition-all cursor-move ${bgColorClass} ${borderColorClass}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-900 truncate">
                          {car.car_brand} {car.car_model}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {car.plate_number}
                          {car.number_of_seats && ` • ${car.number_of_seats} seats`}
                        </div>
                      </div>
                      <div className={`text-xs font-semibold ml-1 ${
                        utilization === 100 ? 'text-green-700' :
                        utilization >= 76 ? 'text-red-700' :
                        utilization >= 51 ? 'text-orange-700' :
                        utilization >= 26 ? 'text-yellow-700' :
                        'text-slate-500'
                      }`}>
                        {utilization}%
                      </div>
                    </div>
                    <div className="mt-1.5 w-full bg-steel-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${
                          utilization === 100 ? 'bg-green-600' :
                          utilization >= 76 ? 'bg-red-500' :
                          utilization >= 51 ? 'bg-orange-500' :
                          utilization >= 26 ? 'bg-yellow-500' :
                          'bg-steel-400'
                        }`}
                        style={{ width: `${utilization}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CarList;
